import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  LockReset as ResetPasswordIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../../components/common/PageHeader';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import usuarioService from '../../services/usuarioService';

const UsuariosList = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isAdmin = user?.rol === 'Administrador';

  // Cargar usuarios y roles
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Cargar usuarios
        const usuariosResponse = await usuarioService.getUsuarios(search);
        setUsuarios(usuariosResponse.data);
        
        // Cargar roles
        const rolesResponse = await usuarioService.getRoles();
        setRoles(rolesResponse.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        showNotification('Error al cargar los usuarios', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey, search, showNotification]);

  // Esquema de validación para crear/editar usuario
  const validationSchema = Yup.object({
    nombre: Yup.string()
      .required('El nombre es requerido')
      .max(100, 'El nombre no puede exceder los 100 caracteres'),
    apellidos: Yup.string()
      .required('Los apellidos son requeridos')
      .max(100, 'Los apellidos no pueden exceder los 100 caracteres'),
    email: Yup.string()
      .email('Email inválido')
      .required('El email es requerido')
      .max(100, 'El email no puede exceder los 100 caracteres'),
    username: Yup.string()
      .min(3, 'El nombre de usuario debe tener al menos 3 caracteres')
      .max(50, 'El nombre de usuario no puede exceder los 50 caracteres'),
    password: editMode ? 
      Yup.string() : 
      Yup.string()
        .required('La contraseña es requerida')
        .min(6, 'La contraseña debe tener al menos 6 caracteres'),
    rol_id: Yup.number()
      .required('El rol es requerido')
  });

  // Esquema de validación para cambiar contraseña
  const passwordValidationSchema = Yup.object({
    password_actual: Yup.string()
      .required('La contraseña actual es requerida'),
    password_nueva: Yup.string()
      .required('La nueva contraseña es requerida')
      .min(6, 'La contraseña debe tener al menos 6 caracteres'),
    password_confirmacion: Yup.string()
      .required('Confirme la nueva contraseña')
      .oneOf([Yup.ref('password_nueva')], 'Las contraseñas no coinciden')
  });

  // Formik para crear/editar usuario
  const formik = useFormik({
    initialValues: {
      nombre: '',
      apellidos: '',
      email: '',
      username: '',
      password: '',
      rol_id: '',
      activo: true
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (editMode) {
          await usuarioService.updateUsuario(selectedUsuario.id, values);
          showNotification('Usuario actualizado con éxito', 'success');
        } else {
          await usuarioService.createUsuario(values);
          showNotification('Usuario creado con éxito', 'success');
        }
        setDialogOpen(false);
        setRefreshKey(prev => prev + 1);
      } catch (error) {
        console.error('Error saving user:', error);
        const errorMsg = error.response?.data?.message || 
          `Error al ${editMode ? 'actualizar' : 'crear'} el usuario`;
        showNotification(errorMsg, 'error');
      }
    },
    validateOnChange: true,
    validateOnBlur: true
  });

  // Formik para cambiar contraseña
  const passwordFormik = useFormik({
    initialValues: {
      password_actual: '',
      password_nueva: '',
      password_confirmacion: ''
    },
    validationSchema: passwordValidationSchema,
    onSubmit: async (values) => {
      try {
        await usuarioService.updatePassword(selectedUsuario.id, {
          password_actual: values.password_actual,
          password_nueva: values.password_nueva
        });
        showNotification('Contraseña actualizada con éxito', 'success');
        setPasswordDialogOpen(false);
      } catch (error) {
        console.error('Error updating password:', error);
        const errorMsg = error.response?.data?.message || 'Error al actualizar la contraseña';
        showNotification(errorMsg, 'error');
      }
    }
  });

  // Abrir diálogo de creación
  const handleCreateClick = () => {
    setEditMode(false);
    formik.resetForm();
    setDialogOpen(true);
  };

  // Abrir diálogo de edición
  const handleEditClick = (usuario) => {
    setEditMode(true);
    setSelectedUsuario(usuario);
    formik.setValues({
      nombre: usuario.nombre,
      apellidos: usuario.apellidos,
      email: usuario.email,
      username: usuario.username,
      password: '', // No se envía la contraseña actual
      rol_id: usuario.rol_id,
      activo: usuario.activo
    });
    setDialogOpen(true);
  };

  // Abrir diálogo de cambio de contraseña
  const handlePasswordClick = (usuario) => {
    setSelectedUsuario(usuario);
    passwordFormik.resetForm();
    setPasswordDialogOpen(true);
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteClick = (usuario) => {
    setSelectedUsuario(usuario);
    setDeleteDialogOpen(true);
  };

  // Confirmar eliminación
  const handleDeleteConfirm = async () => {
    try {
      await usuarioService.deleteUsuario(selectedUsuario.id);
      showNotification('Usuario desactivado correctamente', 'success');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Error al desactivar el usuario', 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Toggle para mostrar/ocultar contraseña
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Obtener nombre del rol
  const getRolName = (rolId) => {
    const rol = roles.find(r => r.id === rolId);
    return rol ? rol.nombre : 'Desconocido';
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Gestión de Usuarios"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Configuración', to: '/configuracion' },
          { text: 'Usuarios' }
        ]}
        actionButton={
          isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              Nuevo Usuario
            </Button>
          )
        }
      />

      <Card>
        <CardContent>
          <Box mb={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar usuarios por nombre, apellidos o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : usuarios.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No se encontraron usuarios{search ? ' con los criterios de búsqueda.' : '.'}
              </Typography>
              {isAdmin && !search && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                  sx={{ mt: 2 }}
                >
                  Crear Nuevo Usuario
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.map((usuario) => (
                    <TableRow key={usuario.id}>
                      <TableCell>
                        {usuario.nombre} {usuario.apellidos}
                      </TableCell>
                      <TableCell>{usuario.email}</TableCell>
                      <TableCell>{usuario.username}</TableCell>
                      <TableCell>
                        <Chip
                          label={getRolName(usuario.rol_id)}
                          color={usuario.rol_id === 1 ? "primary" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {usuario.activo ? (
                          <Chip
                            icon={<ActiveIcon fontSize="small" />}
                            label="Activo"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<InactiveIcon fontSize="small" />}
                            label="Inactivo"
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          {/* Botón de cambiar contraseña */}
                          <Tooltip title="Cambiar contraseña">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handlePasswordClick(usuario)}
                              sx={{ mr: 1 }}
                            >
                              <ResetPasswordIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {/* Botón de editar */}
                          {isAdmin && (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditClick(usuario)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {/* Botón de eliminar/desactivar */}
                          {isAdmin && usuario.id !== user.id && (
                            <Tooltip title="Desactivar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(usuario)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para crear/editar usuario */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {editMode ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="nombre"
                  name="nombre"
                  label="Nombre *"
                  value={formik.values.nombre}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                  helperText={formik.touched.nombre && formik.errors.nombre}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="apellidos"
                  name="apellidos"
                  label="Apellidos *"
                  value={formik.values.apellidos}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.apellidos && Boolean(formik.errors.apellidos)}
                  helperText={formik.touched.apellidos && formik.errors.apellidos}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email *"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="username"
                  name="username"
                  label="Nombre de usuario"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={(formik.touched.username && formik.errors.username) || "Si se deja vacío, se usará el email"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="rol-label">Rol *</InputLabel>
                  <Select
                    labelId="rol-label"
                    id="rol_id"
                    name="rol_id"
                    value={formik.values.rol_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.rol_id && Boolean(formik.errors.rol_id)}
                    label="Rol *"
                  >
                    {roles.map((rol) => (
                      <MenuItem key={rol.id} value={rol.id}>{rol.nombre}</MenuItem>
                    ))}
                  </Select>
                  {formik.touched.rol_id && formik.errors.rol_id && (
                    <FormHelperText error>{formik.errors.rol_id}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="activo-label">Estado</InputLabel>
                  <Select
                    labelId="activo-label"
                    id="activo"
                    name="activo"
                    value={formik.values.activo}
                    onChange={formik.handleChange}
                    label="Estado"
                  >
                    <MenuItem value={true}>Activo</MenuItem>
                    <MenuItem value={false}>Inactivo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {!editMode && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label="Contraseña *"
                    type={showPassword ? 'text' : 'password'}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="secondary">
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <CircularProgress size={24} />
              ) : editMode ? (
                'Actualizar'
              ) : (
                'Crear'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo para cambiar contraseña */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={passwordFormik.handleSubmit}>
          <DialogTitle>Cambiar Contraseña</DialogTitle>
          <DialogContent>
            <Typography variant="body2" paragraph sx={{ mb: 2 }}>
              Cambiando contraseña para: {selectedUsuario?.nombre} {selectedUsuario?.apellidos}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="password_actual"
                  name="password_actual"
                  label="Contraseña actual *"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordFormik.values.password_actual}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={passwordFormik.touched.password_actual && Boolean(passwordFormik.errors.password_actual)}
                  helperText={passwordFormik.touched.password_actual && passwordFormik.errors.password_actual}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="password_nueva"
                  name="password_nueva"
                  label="Nueva contraseña *"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordFormik.values.password_nueva}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={passwordFormik.touched.password_nueva && Boolean(passwordFormik.errors.password_nueva)}
                  helperText={passwordFormik.touched.password_nueva && passwordFormik.errors.password_nueva}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowNewPassword}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="password_confirmacion"
                  name="password_confirmacion"
                  label="Confirmar nueva contraseña *"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordFormik.values.password_confirmacion}
                  onChange={passwordFormik.handleChange}
                  onBlur={passwordFormik.handleBlur}
                  error={passwordFormik.touched.password_confirmacion && Boolean(passwordFormik.errors.password_confirmacion)}
                  helperText={passwordFormik.touched.password_confirmacion && passwordFormik.errors.password_confirmacion}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPasswordDialogOpen(false)} color="secondary">
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={passwordFormik.isSubmitting}
            >
              {passwordFormik.isSubmitting ? <CircularProgress size={24} /> : 'Cambiar Contraseña'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmación para eliminar/desactivar */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Desactivar Usuario"
        content={`¿Estás seguro de que deseas desactivar al usuario "${selectedUsuario?.nombre} ${selectedUsuario?.apellidos}"? El usuario no podrá acceder al sistema después de esta acción.`}
      />
    </Box>
  );
};

export default UsuariosList;